import { useEffect, useState, type FormEvent } from 'react'
import { api, ApiFailure, type ClinicSettings } from '../api'

const BLANK: Omit<ClinicSettings, 'id'> = {
  clinicName: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
  phone: '', altPhone: '', email: '', opdTimings: '', currency: 'INR',
  financialYearStartMonth: 4, receiptPrefix: 'RCP', patientCodePrefix: 'CLN',
  defaultSlotMinutes: 10,
}

export default function Settings() {
  const [form, setForm] = useState<Omit<ClinicSettings, 'id'>>({ ...BLANK })
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ settings: ClinicSettings | null }>('/api/settings')
      if (res.settings) {
        const { id: _id, ...rest } = res.settings
        setForm({ ...BLANK, ...rest })
      }
      setLoaded(true)
    })()
  }, [])

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      await api.put('/api/settings', form)
      setMsg({ kind: 'ok', text: 'Clinic settings saved.' })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not save.' })
    } finally {
      setBusy(false)
    }
  }

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  if (!loaded) return <div className="loading">Loading&hellip;</div>

  return (
    <>
      <div className="page-head">
        <h2>Clinic settings</h2>
        <p>These details print in the letterhead header and drive receipt and patient numbering.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <form onSubmit={(e) => void save(e)}>
        <div className="panel">
          <h3>Identity</h3>
          <div className="grid two">
            <div className="field">
              <label htmlFor="name">Clinic name</label>
              <input id="name" value={form.clinicName}
                onChange={(e) => set('clinicName', e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="timings">OPD timings</label>
              <input id="timings" value={form.opdTimings ?? ''}
                onChange={(e) => set('opdTimings', e.target.value)}
                placeholder="Mon-Sat  9:00 am - 1:00 pm,  5:00 pm - 8:00 pm" />
            </div>
            <div className="field">
              <label htmlFor="addr1">Address line 1</label>
              <input id="addr1" value={form.addressLine1 ?? ''}
                onChange={(e) => set('addressLine1', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="addr2">Address line 2</label>
              <input id="addr2" value={form.addressLine2 ?? ''}
                onChange={(e) => set('addressLine2', e.target.value)} />
            </div>
          </div>
          <div className="grid three" style={{ marginTop: 14 }}>
            <div className="field">
              <label htmlFor="city">City</label>
              <input id="city" value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="state">State</label>
              <input id="state" value={form.state ?? ''} onChange={(e) => set('state', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pin">PIN</label>
              <input id="pin" value={form.pincode ?? ''} onChange={(e) => set('pincode', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="alt">Alternate phone</label>
              <input id="alt" value={form.altPhone ?? ''} onChange={(e) => set('altPhone', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="panel">
          <h3>Numbering &amp; scheduling</h3>
          <p className="panel-note">
            Receipt numbers restart each financial year. April is the Indian default.
          </p>
          <div className="grid three">
            <div className="field">
              <label htmlFor="pcode">Patient code prefix</label>
              <input id="pcode" value={form.patientCodePrefix}
                onChange={(e) => set('patientCodePrefix', e.target.value)} />
              <span className="hint">e.g. {form.patientCodePrefix || 'CLN'}-2026-00417</span>
            </div>
            <div className="field">
              <label htmlFor="rcode">Receipt prefix</label>
              <input id="rcode" value={form.receiptPrefix}
                onChange={(e) => set('receiptPrefix', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="fy">Financial year starts (month)</label>
              <input id="fy" type="number" min={1} max={12} value={form.financialYearStartMonth}
                onChange={(e) => set('financialYearStartMonth', Number(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="slot">Default slot length (min)</label>
              <input id="slot" type="number" min={5} max={120} value={form.defaultSlotMinutes}
                onChange={(e) => set('defaultSlotMinutes', Number(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="cur">Currency</label>
              <input id="cur" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="panel">
          <h3>Modules</h3>
          <label className="check" htmlFor="appts">
            <input id="appts" type="checkbox" checked={form.appointmentsEnabled !== false}
              onChange={(e) => setForm({ ...form, appointmentsEnabled: e.target.checked })} />
            <span><b>Appointments</b>{' '}
              <span className="subtle">{form.appointmentsEnabled !== false
                ? 'On: patients and the desk can book doctor visits ahead of time.'
                : 'Off: the clinic runs on billing alone. Booking screens are hidden and new bookings are refused.'}</span>
            </span>
          </label>
        </div>

        <div className="actions">
          <button type="submit" disabled={busy}>Save settings</button>
        </div>
      </form>
    </>
  )
}
